/**
 * Sign Up Screen
 * User registration with prioritized options:
 * 1. Magic Link (primary - lowest friction)
 * 2. Google OAuth (secondary - coming soon)
 * 3. Email + Password (tertiary - highest friction)
 */

import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Pressable,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useAuthStore } from '../../store/authStore';
import { authApi } from '../../api/endpoints/auth';
import { OAUTH_CONFIG } from '../../utils/constants';
import { validateEmail, validatePassword } from '../../utils/validators';
import Button from '../common/Button';
import Input from '../common/Input';
import ErrorMessage from '../common/ErrorMessage';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import logger from '../../utils/logger';

interface SignUpScreenProps {
  navigation?: any;
}

type SignUpMode = 'options' | 'magic_link' | 'email_password';

export default function SignUpScreen({ navigation }: SignUpScreenProps) {
  // Mode selection
  const [mode, setMode] = useState<SignUpMode>('options');

  // Magic link state
  const [magicEmail, setMagicEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // Email/password state
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Common state
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { setAuth } = useAuthStore();

  // =====================
  // Magic Link Flow
  // =====================

  const validateMagicEmail = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!magicEmail.trim()) {
      newErrors.magicEmail = 'Email requis';
    } else {
      const emailValidation = validateEmail(magicEmail);
      if (!emailValidation.valid) {
        newErrors.magicEmail = emailValidation.error || 'Email invalide';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleMagicLink = async () => {
    setApiError(null);
    setErrors({});

    if (!validateMagicEmail()) {
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.requestMagicLink({
        email: magicEmail.trim().toLowerCase(),
      });

      if (response.success) {
        setMagicLinkSent(true);
      }
    } catch (error: any) {
      logger.error('Magic link error:', error);
      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        const firstError = Object.values(apiErrors)[0];
        setApiError(
          Array.isArray(firstError) ? firstError[0] : String(firstError)
        );
      } else {
        setApiError('Erreur lors de l\'envoi. Veuillez reessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  // =====================
  // Email/Password Flow
  // =====================

  const validateEmailPassword = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!login.trim()) {
      newErrors.login = 'Email requis';
    } else {
      const emailValidation = validateEmail(login);
      if (!emailValidation.valid) {
        newErrors.login = emailValidation.error || 'Email invalide';
      }
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      newErrors.password = passwordValidation.error || 'Mot de passe invalide';
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailPasswordSignUp = async () => {
    setApiError(null);
    setErrors({});

    if (!validateEmailPassword()) {
      return;
    }

    if (!OAUTH_CONFIG.CLIENT_ID || !OAUTH_CONFIG.CLIENT_SECRET) {
      setApiError('Configuration OAuth manquante.');
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.signUp({
        login: login.trim(),
        password,
        client_id: OAUTH_CONFIG.CLIENT_ID,
        client_secret: OAUTH_CONFIG.CLIENT_SECRET,
      });

      await setAuth({
        token: response.token,
        refresh_token: response.refresh_token || response.token,
        user_id: response.user_id,
        username: response.username,
        email: response.email,
        phone_number: response.phone_number,
        first_name: response.first_name,
        last_name: response.last_name,
      });
    } catch (error: any) {
      logger.error('Sign up error:', error);
      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;
        const formattedErrors = Object.entries(apiErrors)
          .map(([field, messages]) => {
            const messageList = Array.isArray(messages) ? messages : [messages];
            return messageList.map(msg => `${field}: ${msg}`).join('\n');
          })
          .join('\n');
        setApiError(formattedErrors);
      } else if (error.response?.data?.error) {
        setApiError(error.response.data.error);
      } else {
        setApiError('Erreur lors de l\'inscription. Veuillez reessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  // =====================
  // Render: Magic Link Sent
  // =====================

  if (magicLinkSent) {
    return (
      <View style={styles.container}>
        <View style={styles.successContainer}>
          <View style={styles.successIcon}>
            <Icon name="email-check-outline" size={64} color={colors.primary} />
          </View>
          <Text style={styles.successTitle}>Verifie ta boite mail</Text>
          <Text style={styles.successMessage}>
            Un lien de connexion a ete envoye a{'\n'}
            <Text style={styles.successEmail}>{magicEmail}</Text>
          </Text>
          <Text style={styles.successHint}>
            Clique sur le lien dans l'email pour te connecter.{'\n'}
            Le lien expire dans 15 minutes.
          </Text>

          <Button
            title="Renvoyer le lien"
            onPress={handleMagicLink}
            variant="outline"
            loading={loading}
            style={styles.resendButton}
          />

          <Button
            title="Utiliser un autre email"
            onPress={() => {
              setMagicLinkSent(false);
              setMagicEmail('');
            }}
            variant="text"
          />
        </View>
      </View>
    );
  }

  // =====================
  // Render: Magic Link Form
  // =====================

  if (mode === 'magic_link') {
    return (
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.content}>
            <Pressable onPress={() => setMode('options')} style={styles.backButton}>
              <Icon name="arrow-left" size={24} color={colors.text} />
            </Pressable>

            <View style={styles.iconContainer}>
              <Icon name="email-fast-outline" size={48} color={colors.primary} />
            </View>

            <Text style={styles.title}>Connexion par email</Text>
            <Text style={styles.subtitle}>
              Entre ton email, on t'envoie un lien magique.{'\n'}
              Pas de mot de passe a retenir !
            </Text>

            <View style={styles.form}>
              <Input
                label="Email"
                value={magicEmail}
                onChangeText={setMagicEmail}
                placeholder="ton@email.com"
                autoCapitalize="none"
                keyboardType="email-address"
                autoComplete="email"
                autoFocus
                error={errors.magicEmail}
                editable={!loading}
              />

              <ErrorMessage error={apiError} />

              <Button
                title="Recevoir mon lien"
                onPress={handleMagicLink}
                loading={loading}
                disabled={loading}
                fullWidth
                style={styles.button}
              />
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    );
  }

  // =====================
  // Render: Email/Password Form
  // =====================

  if (mode === 'email_password') {
    return (
      <KeyboardAvoidingView
        style={styles.container}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.content}>
            <Pressable onPress={() => setMode('options')} style={styles.backButton}>
              <Icon name="arrow-left" size={24} color={colors.text} />
            </Pressable>

            <Text style={styles.title}>Creer un compte</Text>
            <Text style={styles.subtitle}>
              Avec email et mot de passe
            </Text>

            <View style={styles.form}>
              <Input
                label="Email"
                value={login}
                onChangeText={setLogin}
                placeholder="ton@email.com"
                autoCapitalize="none"
                keyboardType="email-address"
                autoComplete="email"
                error={errors.login}
                editable={!loading}
              />

              <Input
                label="Mot de passe"
                value={password}
                onChangeText={setPassword}
                placeholder="Minimum 6 caracteres"
                secureTextEntry
                autoCapitalize="none"
                autoComplete="password-new"
                error={errors.password}
                editable={!loading}
              />

              <Input
                label="Confirmer le mot de passe"
                value={confirmPassword}
                onChangeText={setConfirmPassword}
                placeholder="Repetez le mot de passe"
                secureTextEntry
                autoCapitalize="none"
                autoComplete="password-new"
                error={errors.confirmPassword}
                editable={!loading}
              />

              <ErrorMessage error={apiError} />

              <Button
                title="Creer mon compte"
                onPress={handleEmailPasswordSignUp}
                loading={loading}
                disabled={loading}
                fullWidth
                style={styles.button}
              />
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    );
  }

  // =====================
  // Render: Options Screen (Default)
  // =====================

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.content}>
          <View style={styles.logoContainer}>
            <View style={styles.logoIcon}>
              <Text style={styles.logoEmoji}>🤖</Text>
            </View>
          </View>

          <Text style={styles.title}>Rejoins Wealfi</Text>
          <Text style={styles.subtitle}>
            Ton assistant financier personnel.{'\n'}
            Diagnostic gratuit en 3 minutes.
          </Text>

          <View style={styles.optionsContainer}>
            {/* Option 1: Magic Link (Primary) */}
            <Pressable
              style={[styles.optionButton, styles.optionPrimary]}
              onPress={() => setMode('magic_link')}
            >
              <Icon name="email-fast-outline" size={24} color={'#FFFFFF'} />
              <View style={styles.optionTextContainer}>
                <Text style={styles.optionTitle}>Continuer avec mon email</Text>
                <Text style={styles.optionSubtitle}>Lien magique, sans mot de passe</Text>
              </View>
              <Icon name="chevron-right" size={24} color={'#FFFFFF'} />
            </Pressable>

            {/* Option 2: Google OAuth (Coming Soon) */}
            <Pressable
              style={[styles.optionButton, styles.optionGoogle, styles.optionDisabled]}
              disabled
            >
              <Icon name="google" size={24} color="#9CA3AF" />
              <View style={styles.optionTextContainer}>
                <Text style={[styles.optionTitle, styles.optionTitleDisabled]}>
                  Continuer avec Google
                </Text>
                <Text style={styles.optionSubtitleDisabled}>Bientot disponible</Text>
              </View>
              <View style={styles.comingSoonBadge}>
                <Text style={styles.comingSoonText}>Soon</Text>
              </View>
            </Pressable>

            {/* Divider */}
            <View style={styles.divider}>
              <View style={styles.dividerLine} />
              <Text style={styles.dividerText}>ou</Text>
              <View style={styles.dividerLine} />
            </View>

            {/* Option 3: Email + Password (Tertiary) */}
            <Pressable
              style={styles.emailPasswordLink}
              onPress={() => setMode('email_password')}
            >
              <Text style={styles.emailPasswordText}>
                Creer un compte avec email et mot de passe
              </Text>
              <Icon name="arrow-right" size={16} color={colors.textSecondary} />
            </Pressable>
          </View>

          {/* Already have account */}
          <View style={styles.loginContainer}>
            <Text style={styles.loginText}>Deja un compte ?</Text>
            <Pressable onPress={() => navigation?.navigate('Login')}>
              <Text style={styles.loginLink}>Se connecter</Text>
            </Pressable>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingVertical: spacing.xl,
  },
  content: {
    padding: spacing.xl,
  },

  // Logo
  logoContainer: {
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  logoIcon: {
    width: 80,
    height: 80,
    borderRadius: 20,
    backgroundColor: '#E8F5E0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoEmoji: {
    fontSize: 40,
  },

  // Header
  title: {
    fontSize: typography.fontSize.xxl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  subtitle: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.xl,
    lineHeight: 24,
  },

  // Options
  optionsContainer: {
    marginTop: spacing.lg,
  },
  optionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    borderRadius: 12,
    marginBottom: spacing.sm,
  },
  optionPrimary: {
    backgroundColor: colors.primary,
  },
  optionGoogle: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  optionDisabled: {
    opacity: 0.6,
  },
  optionTextContainer: {
    flex: 1,
    marginLeft: spacing.md,
  },
  optionTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: '#FFFFFF',
  },
  optionTitleDisabled: {
    color: '#9CA3AF',
  },
  optionSubtitle: {
    fontSize: typography.fontSize.sm,
    color: 'rgba(255,255,255,0.8)',
    marginTop: 2,
  },
  optionSubtitleDisabled: {
    fontSize: typography.fontSize.sm,
    color: '#9CA3AF',
    marginTop: 2,
  },
  comingSoonBadge: {
    backgroundColor: '#FEF3C7',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
  comingSoonText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#92400E',
  },

  // Divider
  divider: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: spacing.lg,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: '#E5E7EB',
  },
  dividerText: {
    marginHorizontal: spacing.md,
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },

  // Email/Password Link
  emailPasswordLink: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.sm,
  },
  emailPasswordText: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    marginRight: spacing.xs,
  },

  // Login Link
  loginContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: spacing.xl,
  },
  loginText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
  },
  loginLink: {
    fontSize: typography.fontSize.md,
    color: colors.primary,
    fontWeight: typography.fontWeight.semibold,
    marginLeft: spacing.xs,
  },

  // Form
  form: {
    marginTop: spacing.lg,
  },
  button: {
    marginTop: spacing.md,
  },
  backButton: {
    marginBottom: spacing.lg,
    padding: spacing.xs,
    alignSelf: 'flex-start',
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: spacing.lg,
  },

  // Success State
  successContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  successIcon: {
    marginBottom: spacing.lg,
  },
  successTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    textAlign: 'center',
    marginBottom: spacing.md,
  },
  successMessage: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: spacing.md,
  },
  successEmail: {
    color: colors.primary,
    fontWeight: typography.fontWeight.semibold,
  },
  successHint: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: spacing.xl,
  },
  resendButton: {
    marginBottom: spacing.sm,
  },
});
